import { useCallback } from "react";

import { useImperativeModal } from "@shapr3d/frontend-lib";

import { PaywallModal } from "../components/Headers/components/ShareModal/components/PaywallModal";
import messages from "../locales/messages";
import { SubscriptionPeriod } from "../types";
import useNavigation from "./navigation";
import { useTracker } from "./useTracker";
import useTranslation from "./useTranslation";

const PAYWALL_CONFIGS = {
  download_shapr_file: {
    utmSource: "download_shapr_file",
    period: SubscriptionPeriod.Yearly,
    showPeriodPicker: true,
    owner: {
      titleMessage: messages.Paywall.DownloadShaprFile.Owner.Title,
      descriptionMessage: messages.Paywall.DownloadShaprFile.Owner.Description,
      showSubscriptionButton: true,
      size: "small",
    },
    nonOwner: {
      titleMessage: messages.Paywall.DownloadShaprFile.NonOwner.Title,
      descriptionMessage: messages.Paywall.DownloadShaprFile.NonOwner.Description,
      showSubscriptionButton: false,
      size: "medium",
    },
  },
  project_editor_role: {
    utmSource: "project_editor_role",
    period: SubscriptionPeriod.Yearly,
    showPeriodPicker: true,
    owner: {
      titleMessage: messages.Paywall.ProjectEditorRole.Owner.Title,
      descriptionMessage: messages.Paywall.ProjectEditorRole.Owner.Description,
      showSubscriptionButton: true,
      size: "small",
    },
    nonOwner: {
      titleMessage: messages.Paywall.ProjectEditorRole.NonOwner.Title,
      descriptionMessage: messages.Paywall.ProjectEditorRole.NonOwner.Description,
      showSubscriptionButton: false,
      size: "medium",
    },
  },
} as const;

export type PaywallId = keyof typeof PAYWALL_CONFIGS;

type PaywallOpenOptions = {
  isOwner: boolean;
  remoteProjectId?: string;
  returnToURL?: string;
};

export const usePaywall = (id: PaywallId) => {
  const tracker = useTracker();
  const navigation = useNavigation();
  const { t } = useTranslation();
  const modal = useImperativeModal();

  const open = useCallback(
    ({ isOwner, remoteProjectId, returnToURL }: PaywallOpenOptions) => {
      const config = PAYWALL_CONFIGS[id];
      const variantConfig = isOwner ? config.owner : config.nonOwner;
      const title = t(variantConfig.titleMessage);
      const description = t(variantConfig.descriptionMessage);

      const handleDismiss = () => {
        if (!remoteProjectId) return;
        tracker.trackPaywallModalDismissed({
          paywallReason: id,
          remoteProjectId,
        });
      };

      const handleSubscriptionClick = () => {
        navigation.goToCheckout({
          period: config.period,
          showPeriodPicker: config.showPeriodPicker,
          utmSource: config.utmSource,
          returnToURL,
        });

        if (!remoteProjectId) return;
        tracker.trackPaywallSubscribeClicked({
          paywallReason: id,
          remoteProjectId,
        });
      };

      if (variantConfig.showSubscriptionButton) {
        modal.open((managedProps) => (
          <PaywallModal
            modalProps={managedProps}
            title={title}
            description={description}
            showSubscriptionButton={true}
            size={variantConfig.size}
            onClose={handleDismiss}
            onClick={handleSubscriptionClick}
          />
        ));
      } else {
        modal.open((managedProps) => (
          <PaywallModal
            modalProps={managedProps}
            title={title}
            description={description}
            showSubscriptionButton={false}
            size={variantConfig.size}
            onClose={handleDismiss}
          />
        ));
      }

      if (!remoteProjectId) return;
      tracker.trackPaywallModalShown({
        remoteProjectId,
        paywallReason: id,
        hasSubscribeAction: isOwner,
      });
    },
    [id, modal, navigation, t, tracker]
  );

  return {
    open,
    close: modal.close,
    isOpen: modal.isOpen,
  };
};
